/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import StrategyAudioPanel from "../components/StrategyAudioPanel";
import { api, API_BASE } from "../lib/api";
import { ArrowRight, ArrowLeft, FilmStrip, Play, Pause, Spinner } from "@phosphor-icons/react";

export function SeriesHub() {
  const [series, setSeries] = useState([]);
  const [continueEp, setContinueEp] = useState(null);

  useEffect(() => {
    api.get("/series").then((r) => setSeries(r.data.series || [])).catch(() => {});
    // Resume card — only fires if user is logged in (auth interceptor sends token)
    api.get("/users/listening/continue")
      .then((r) => setContinueEp(r.data.last_listened))
      .catch(() => {});
  }, []);

  const total = series.reduce((acc, s) => acc + (s.episode_count || 0), 0);

  return (
    <div data-testid="series-hub" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-36 pb-12">
        <p className="eyebrow"><span className="gold-divider" />The Series Library</p>
        <h1 className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95]">
          {total} episodes. Ten series. <span className="gold-text italic">One playbook.</span>
        </h1>
        <p className="text-lg text-exodus-muted mt-8 max-w-2xl leading-relaxed">
          Every concept that matters in private wealth — broken into bite-sized 60-second breakdowns.
          Browse by series, dive into any episode, or queue them in order.
        </p>

        {/* Continue Listening card */}
        {continueEp && (
          <Link
            to={`/series/${continueEp.series_id}?resume=${continueEp.episode_number}`}
            data-testid="continue-listening"
            className="mt-10 block bg-exodus-elevated border border-exodus-gold/60 hover:border-exodus-gold p-5 transition-colors max-w-xl"
          >
            <div className="flex items-center gap-4">
              <Play size={24} weight="fill" className="text-exodus-gold shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="eyebrow text-[10px] text-exodus-gold">Continue listening</p>
                <p className="font-display text-lg mt-1 truncate">
                  {continueEp.episode_title || `Episode ${continueEp.episode_number}`}
                </p>
                <p className="text-xs text-exodus-muted truncate">
                  {continueEp.series_title || continueEp.series_id} · {Math.round((continueEp.percent || 0) * 100)}% complete
                </p>
                {/* progress bar */}
                <div className="mt-2 h-1 bg-exodus-border rounded">
                  <div
                    className="h-1 bg-exodus-gold rounded transition-all"
                    style={{ width: `${Math.min(100, Math.round((continueEp.percent || 0) * 100))}%` }}
                  />
                </div>
              </div>
              <ArrowRight size={16} className="text-exodus-gold shrink-0" />
            </div>
          </Link>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-32">
        <div className="grid md:grid-cols-2 gap-px bg-exodus-border">
          {series.map((s, i) => (
            <Link
              key={s.id}
              to={`/series/${s.id}`}
              data-testid={`series-card-${s.id}`}
              className="group bg-exodus-surface p-8 lg:p-10 hover:bg-exodus-elevated transition-colors flex flex-col"
            >
              <div className="flex items-center gap-3">
                <FilmStrip size={20} className="text-exodus-gold" />
                <p className="eyebrow text-xs text-exodus-muted">Series {String(i + 1).padStart(2, "0")} · {s.episode_count} episodes</p>
              </div>
              <h3 className="font-display text-3xl lg:text-4xl mt-4 leading-tight group-hover:text-exodus-gold transition-colors">
                {s.title}
              </h3>
              <p className="text-sm text-exodus-muted mt-4 leading-relaxed flex-1">{s.summary}</p>
              <div className="flex items-center gap-2 mt-8 text-xs gold-text">
                Open the series
                <ArrowRight size={12} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export function SeriesDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [activeEp, setActiveEp] = useState(null); // episode_number currently playing
  const [loadingEp, setLoadingEp] = useState(null);
  const [progressByEp, setProgressByEp] = useState({}); // {epNum: {position_sec, percent}}
  const audioRef = useRef(null);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    api.get(`/series/${id}`).then((r) => setS(r.data)).catch(() => {});
    // Pull saved progress for this series so we can render per-episode bars
    api.get("/users/listening/all")
      .then((r) => {
        const all = r.data.progress || {};
        const filtered = {};
        Object.keys(all).forEach((k) => {
          const [sid, ep] = k.split("::");
          if (sid === id) filtered[Number(ep)] = all[k];
        });
        setProgressByEp(filtered);
      })
      .catch(() => {});
    return () => {
      if (audioRef.current) {
        // Final save on unmount
        _saveProgress();
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Read ?resume=<epNum> from URL on first load
  useEffect(() => {
    if (!s) return;
    const params = new URLSearchParams(window.location.search);
    const resumeEp = parseInt(params.get("resume"), 10);
    if (resumeEp && !activeEp) {
      togglePlay(resumeEp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s]);

  const _saveProgress = () => {
    const a = audioRef.current;
    if (!a || !s || !activeEp) return;
    const pos = a.currentTime || 0;
    const dur = a.duration || 0;
    if (pos < 2) return; // skip noise
    api.post("/users/listening/progress", {
      series_id: s.id,
      episode_number: activeEp,
      position_sec: pos,
      duration_sec: dur,
    }).catch(() => {});
    // Also update local UI immediately
    setProgressByEp((prev) => ({
      ...prev,
      [activeEp]: { position_sec: pos, duration_sec: dur, percent: dur ? pos / dur : 0 },
    }));
  };

  const togglePlay = (epNum) => {
    if (!s) return;
    if (activeEp === epNum && audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
      } else {
        _saveProgress();
        audioRef.current.pause();
        setActiveEp(null);
      }
      return;
    }
    // Switch to a new episode — save the outgoing one first
    if (audioRef.current) {
      _saveProgress();
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (saveTimerRef.current) clearInterval(saveTimerRef.current);

    setLoadingEp(epNum);
    const url = `${API_BASE}/series/${s.id}/episode/${epNum}/audio`;
    const audio = new Audio(url);
    audioRef.current = audio;

    // Seek to saved position (if any) once metadata loads
    const saved = progressByEp[epNum];
    if (saved && saved.position_sec && (saved.percent || 0) < 0.95) {
      audio.onloadedmetadata = () => {
        try { audio.currentTime = saved.position_sec; } catch (_e) { /* ignore seek errors */ }
      };
    }

    audio.onplaying = () => {
      setLoadingEp(null);
      setActiveEp(epNum);
      // Start the 10-second save loop
      saveTimerRef.current = setInterval(_saveProgress, 10000);
    };
    audio.onpause = () => {
      _saveProgress();
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    };
    audio.onended = () => {
      _saveProgress();
      setActiveEp(null);
      if (saveTimerRef.current) clearInterval(saveTimerRef.current);
    };
    audio.onerror = () => { setLoadingEp(null); setActiveEp(null); };
    audio.play().catch(() => { setLoadingEp(null); setActiveEp(null); });
  };

  if (!s) {
    return (
      <div className="min-h-screen bg-exodus-bg text-exodus-text">
        <Nav />
        <div className="max-w-3xl mx-auto px-6 pt-36">
          <p className="text-exodus-muted">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="series-detail" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <section className="max-w-4xl mx-auto px-6 lg:px-12 pt-36 pb-12">
        <Link to="/series" className="inline-flex items-center gap-2 text-sm text-exodus-muted hover:text-exodus-gold transition-colors">
          <ArrowLeft size={14} /> All series
        </Link>
        <p className="eyebrow mt-8"><span className="gold-divider" />Series · {s.episodes.length} episodes</p>
        <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[0.95]">{s.title}</h1>
        <p className="text-lg text-exodus-muted mt-6 max-w-2xl leading-relaxed">{s.summary}</p>
        <p className="text-xs text-exodus-muted mt-4 italic">Tap any play button to hear Leon narrate the episode.</p>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-12 pb-32">
        <div className="border-t border-exodus-border">
          {s.episodes.map((ep) => {
            const isPlaying = activeEp === ep.episode_number;
            const isLoading = loadingEp === ep.episode_number;
            const prog = progressByEp[ep.episode_number];
            const pct = prog ? Math.round((prog.percent || 0) * 100) : 0;
            const completed = pct >= 95;
            return (
              <article
                key={ep.episode_number}
                data-testid={`series-episode-${s.id}-${ep.episode_number}`}
                className={`border-b border-exodus-border py-8 group transition-colors ${isPlaying ? "bg-exodus-elevated" : ""}`}
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-12 text-right">
                    <span className="font-display text-2xl gold-text">{String(ep.episode_number).padStart(2, "0")}</span>
                    {completed && (
                      <p className="text-[10px] uppercase tracking-wider text-emerald-400 mt-1">Done</p>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-2xl leading-tight">{ep.title}</h3>
                    <p className="text-sm text-exodus-gold italic mt-2">{ep.hook}</p>
                    <p className="text-sm text-exodus-muted mt-3 leading-relaxed">{ep.body}</p>
                    {prog && pct > 0 && !completed && (
                      <div
                        className="mt-3 flex items-center gap-3"
                        data-testid={`progress-${s.id}-${ep.episode_number}`}
                      >
                        <div className="flex-1 h-1 bg-exodus-border rounded">
                          <div
                            className="h-1 bg-exodus-gold rounded transition-all"
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-exodus-muted font-mono shrink-0">{pct}%</span>
                      </div>
                    )}
                  </div>
                  <button
                    data-testid={`series-play-${s.id}-${ep.episode_number}`}
                    onClick={() => togglePlay(ep.episode_number)}
                    aria-label={isPlaying ? "Pause episode" : "Play episode"}
                    className={`flex-shrink-0 w-12 h-12 flex items-center justify-center border transition-all ${
                      isPlaying
                        ? "border-exodus-gold bg-exodus-gold text-exodus-bg"
                        : "border-exodus-border text-exodus-muted hover:border-exodus-gold hover:text-exodus-gold"
                    }`}
                  >
                    {isLoading ? (
                      <Spinner size={18} className="animate-spin" />
                    ) : isPlaying ? (
                      <Pause size={18} weight="fill" />
                    ) : (
                      <Play size={18} weight="fill" />
                    )}
                  </button>
                </div>

                {/* STRATEGY companion — only renders for the currently-active episode */}
                {isPlaying && (
                  <StrategyAudioPanel
                    sourcePath={`/series/${s.id}/episode/${ep.episode_number}/strategy-audio`}
                    testIdPrefix={`series-strategy-${s.id}-${ep.episode_number}`}
                  />
                )}
              </article>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
}
